import { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'Components/atoms';
import ChatMessage from 'Components/molecules/ChatMessage/ChatMessage';
import './AnalyticsAmpyChatPanel.scss';

export type AnalyticsChatMessage = {
    id: string;
    sender: 'user' | 'ampy';
    message: string;
};

type AnalyticsAmpyChatPanelProps = {
    messages: AnalyticsChatMessage[];
    pending: boolean;
    pendingAcknowledgement?: string | null;
    error: string | null;
    onSend: (message: string) => Promise<void> | void;
    showHeader?: boolean;
};

const QUICK_PROMPTS = [
    "Summarize the overall security posture of the organization.",
    "How many users haven't completed compliance training?",
    "How many users do not have an attributed asset?",
    "Show me the top risky components in the last 30 days.",
    "Which departments have the highest number of open findings?",
    "How many users have open critical vulnerabilities?",
];

const ThinkingIndicator = () => (
    <div className="analytics-chat-panel__thinking" data-testid="analytics-chat-thinking">
        <div className="analytics-chat-panel__thinking-avatar">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" fill="currentColor" />
            </svg>
        </div>
        <div className="analytics-chat-panel__thinking-bubble">
            <span className="analytics-chat-panel__thinking-dot" />
            <span className="analytics-chat-panel__thinking-dot" />
            <span className="analytics-chat-panel__thinking-dot" />
        </div>
    </div>
);

export const AnalyticsAmpyChatPanel = ({ messages, pending, pendingAcknowledgement = null, error, onSend, showHeader = true }: AnalyticsAmpyChatPanelProps) => {
    const [input, setInput] = useState('');
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
    }, [messages, pending, pendingAcknowledgement]);

    const disableSend = useMemo(() => pending || input.trim().length === 0, [input, pending]);

    const sendInput = () => {
        const trimmed = input.trim();
        if (!trimmed || pending) {
            return;
        }
        setInput('');
        onSend(trimmed);
    };

    return (
        <div className="analytics-chat-panel" data-testid="analytics-ampy-chat-panel">
            {showHeader && (
                <div className="analytics-chat-panel__header">
                    <div className="analytics-chat-panel__header-icon">
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" fill="currentColor" />
                        </svg>
                    </div>
                    <div>
                        <Typography variant="h4" color="text-high-emphasis">
                            Ask Ampy
                        </Typography>
                        <Typography variant="body6" color="text-medium-emphasis">
                            Ask questions about your analytics data in plain English.
                        </Typography>
                    </div>
                </div>
            )}

            {messages.length === 0 && (
                <div className="analytics-chat-panel__quick-prompts">
                    {QUICK_PROMPTS.map((prompt, idx) => (
                        <button
                            key={`analytics-chat-prompt-${idx}`}
                            type="button"
                            className="analytics-chat-panel__quick-prompt"
                            onClick={() => {
                                setInput('');
                                onSend(prompt);
                            }}
                            disabled={pending}
                            data-testid={`analytics-chat-quick-prompt-${idx}`}
                        >
                            {prompt}
                        </button>
                    ))}
                </div>
            )}

            <div
                ref={messagesContainerRef}
                className="analytics-chat-panel__messages custom-scrollbar"
                data-testid="analytics-chat-messages"
            >
                {messages.length === 0 ? (
                    <div className="analytics-chat-panel__empty">
                        <Typography variant="body5" color="text-low-emphasis">
                            Try a question about findings, users, components, or assets.
                        </Typography>
                    </div>
                ) : (
                    messages.map((message) => (
                        <div
                            key={message.id}
                            className={`analytics-chat-panel__message analytics-chat-panel__message--${message.sender}`}
                        >
                            <ChatMessage sender={message.sender === 'ampy' ? 'ai' : 'user'} message={message.message} />
                        </div>
                    ))
                )}
                {pending && pendingAcknowledgement ? (
                    <div className="analytics-chat-panel__message analytics-chat-panel__message--ampy">
                        <ChatMessage sender="ai" message={pendingAcknowledgement} />
                    </div>
                ) : null}
                {pending && <ThinkingIndicator />}
            </div>

            {error ? (
                <Typography
                    variant="body6"
                    color="text-medium-emphasis"
                    className="analytics-chat-panel__error"
                    data-testid="analytics-chat-error"
                >
                    {error}
                </Typography>
            ) : null}

            <div className="analytics-chat-panel__composer">
                <textarea
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            sendInput();
                        }
                    }}
                    placeholder={pending ? 'Ampy is thinking...' : 'Ask Ampy a question...'}
                    className="analytics-chat-panel__input"
                    disabled={pending}
                    rows={1}
                    data-testid="analytics-chat-input"
                />
                <button
                    type="button"
                    onClick={sendInput}
                    disabled={disableSend}
                    className="analytics-chat-panel__send-btn"
                    data-testid="analytics-chat-send"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default AnalyticsAmpyChatPanel;
